import React from 'react'
import PropTypes from 'prop-types'
import { withStyles } from '@material-ui/core/styles'
import { Link } from 'react-router-dom'
import Card from '@material-ui/core/Card'
import CardActions from '@material-ui/core/CardActions'
import CardContent from '@material-ui/core/CardContent'
import CardHeader from '@material-ui/core/CardHeader'
import IconButton from '@material-ui/core/IconButton'
import Table from '@material-ui/core/Table'
import TableBody from '@material-ui/core/TableBody'
import TextField from '@material-ui/core/TextField'
import SearchIcon from '@material-ui/icons/Search'
import { BodyCustomTableCell, HeadCustomTableCell } from '../custom-material/table/TableCell'
import { LongCustomButton } from '../custom-material/button/Button'
import CustomTableHead from '../custom-material/table/TableHead'
import CustomTableRow from '../custom-material/table/TableRow'
import CustomTypography from '../custom-material/typography/Typography'
import { isEmpty } from 'lodash'
import EmptyStateComponent from '../common/empty-state/EmptyStateComponent'
import { filterObjects, requesterEmailAddress } from '../../libs/utils'
import FileSaver from 'file-saver'

const styles = theme => ({
  manageUsersCard: {
    margin: 16
  },
  manageUsersCardHeader: {
    backgroundColor: '#e1ecf8'
  },
  title: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: 20
  },
  userActionContainer: {
    display: 'flex',
    height: 48,
    alignItems: 'center',
    justifyContent: 'space-between',
    border: '2px solid #C8C8C7'
  },
  userSearchContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    paddingLeft: 12
  },
  userSearchInput: {
    height: 29,
    minWidth: 250,
    border: '1px solid #2A3D91',
    borderRadius: 2,
    fontSize: 14,
    paddingLeft: 6,
    fontStyle: 'italic'
  },
  userSearchPlaceholder: {
    marginLeft: 8
  },
  deactivateButton: {
    backgroundColor: '#666666',
    color: 'white',
    '&:hover': {
      backgroundColor: '#444444'
    }
  },
  searchIconButton: {
    height: 31,
    width: 42,
    padding: 0,
    marginLeft: 8,
    backgroundColor: '#2075D7',
    color: 'white',
    borderRadius: 2,
    '&:hover': {
      backgroundColor: '#1a5dac'
    }
  },
  userButtonContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    paddingRight: 12
  },
  tableTitle: {
    display: 'flex',
    alignItems: 'center',
    height: 40,
    padding: '0px 8px',
    fontSize: 22,
    backgroundColor: theme.palette.primary.main,
    color: 'white'
  },
  row: {
    '&:nth-of-type(even)': {
      backgroundColor: '#EDF3F8'
    }
  },
  firstRow: {
    borderBottom: `2px solid #2F497B`
  },
  pendingNameCell: {
    width: 125
  },
  cardFooter: {
    padding: '16px 24px'
  },
  privacyStatement: {
    margin: 0,
    fontSize: 13.5
  },
  privacyLink: {
    color: theme.palette.primary.main,
    fontWeight: 500,
    textDecoration: 'none'
  }
})

class ManageUsersComponent extends React.PureComponent {
  constructor (props) {
    super(props)
    this.state = { filter: null }

    this.handleFilterChange = event => this.setState({filter: event.target.value})

    this.sortContacts = contacts => {
      const emailAddress = requesterEmailAddress(this.props)
      contacts = contacts.sort((a, b) => (a.firstName || '').localeCompare(b.firstName))
      return contacts.sort((a, b) => a.emailAddress === emailAddress ? -1 : b.emailAddress === emailAddress ? 1 : 0)
    }

    this.exportToExcel = () => {
      let { contacts } = this.props
      const companies = Object.keys(contacts)
      let contactList = []

      const html = []
      html.push('<html><head><meta charset="UTF-8"></head><body>',
        '<table border=1><thead><tr>',
          '<th>Name</th>',
          '<th>Job Title</th>',
          '<th>Division</th>',
          '<th>Phone Number</th>',
          '<th>Email</th>',
        '</tr></thead><tbody>'
      )

      for (const company of companies) {
        if (company === 'pending') continue
        contactList = [...contactList, ...contacts[company]]
      }

      const sortedContacts = contactList.sort((a, b) => (a.firstName || '').localeCompare(b.firstName))
      for (const contact of sortedContacts) {
        html.push(
          '<tr>',
            `<td>${contact.firstName || ''} ${contact.lastName || ''}</td>`,
            `<td>${contact.jobTitle || ''}</td>`,
            `<td>${contact.department || ''}</td>`,
            `<td>${contact.officePhone || ''}</td>`,
            `<td>${contact.emailAddress || ''}</td>`,
          '</tr>'
        )
      }
      html.push('</tbody></table></body></html>')

      FileSaver.saveAs(new window.Blob([html.join('')], { type: 'vnd.ms-excel' }), `contacts.xls`)
    }
  }

  _filterContacts (contacts, filter) {
    if (!filter) return contacts
    return filterObjects(
      contacts.map(x => ({...x, fullName: `${x.firstName} ${x.lastName}`})),
      filter
    )
  }

  render () {
    const { filter } = this.state
    const { classes, contacts, history } = this.props
    const active = isEmpty(contacts) ? [] : Object.keys(contacts).filter(x => x !== 'pending')
    const pending = isEmpty(contacts) ? [] : Object.keys(contacts).filter(x => x === 'pending')
    const companies = active.concat(pending)
    return (
      <Card className={classes.manageUsersCard}>
        <CardHeader className={classes.manageUsersCardHeader}
          title={<div className={classes.title}><span>Manage Users</span></div>}
        />
        <CardContent>
          <CustomTypography variant='body1' paragraph>The following is a list of the current users on your IPD Analytics Subscription. Feel free to request changes to this list using the Add Users and Deactivate Users buttons.</CustomTypography>
          <div className={classes.userActionContainer}>
            <div className={classes.userSearchContainer}>
              <span style={{marginRight: 8}}>Search</span>
              <TextField
                id='search'
                placeholder='search current users'
                value={filter || ''}
                InputProps={{disableUnderline: true, classes: {root: classes.userSearchInput}}}
                fullWidth
                onChange={this.handleFilterChange}
              />
              <IconButton className={classes.searchIconButton}><SearchIcon /></IconButton>
            </div>
            <div className={classes.userButtonContainer}>
              <Link style={{textDecoration: 'none'}} to={`/add-users${(history.location && history.location.search && history.location.search.split('?email=')[1]) ? `/?email=${history.location.search.split('?email=')[1]}` : ''}`}><LongCustomButton style={{marginRight: 12}} color='secondary' size='small' variant='contained' >Add Users</LongCustomButton></Link>
              <Link style={{textDecoration: 'none'}} to={`/deactivate-users${(history.location && history.location.search && history.location.search.split('?email=')[1]) ? `/?email=${history.location.search.split('?email=')[1]}` : ''}`}><LongCustomButton className={classes.deactivateButton} size='small' variant='contained' >Deactivate Users</LongCustomButton></Link>
              <IconButton style={{marginLeft: 5, padding: 8}} onClick={this.exportToExcel}><img src='/icons/excel.png' alt='Excel Export' /></IconButton>
            </div>
          </div>
          {isEmpty(contacts)
            ? null
            : companies.map((company, companyIndex) => {
              return (
                <React.Fragment key={companyIndex}>
                  {company === 'pending' ? <br /> : null}
                  <CustomTypography className={classes.tableTitle} variant='h6'>{company === 'pending' ? 'Pending Requests:' : `${company} Current Users:`}</CustomTypography>
                  {isEmpty(contacts[company])
                    ? company === 'pending' ? <div style={{border: '1px solid #C8C8C7'}}><EmptyStateComponent title={`There are no ${company === 'pending' ? 'Pending Requests' : 'Current Users'}`} /></div> : null
                    : <Table>
                      <CustomTableHead>
                        <CustomTableRow>
                          <React.Fragment>
                            <HeadCustomTableCell className='th-sm' size='small'>NAME</HeadCustomTableCell>
                            <HeadCustomTableCell className='th-sm' size='small'>JOB TITLE</HeadCustomTableCell>
                            <HeadCustomTableCell className='th-sm' size='small'>DIVISION</HeadCustomTableCell>
                            <HeadCustomTableCell className='th-xs' size='small'>PHONE NUMBER</HeadCustomTableCell>
                            <HeadCustomTableCell className='th-md' size='small'>EMAIL</HeadCustomTableCell>
                            {company !== 'pending' ? null : <HeadCustomTableCell className='th-xs' size='small'>ACTION</HeadCustomTableCell>}
                          </React.Fragment>
                        </CustomTableRow>
                      </CustomTableHead>
                      <TableBody style={isEmpty(contacts[company]) ? {border: '1px solid #C8C8C7'} : {}}>
                        {this.sortContacts(this._filterContacts(contacts[company], filter)).map((contact, contactIndex) => (
                          <CustomTableRow key={contactIndex} className={`${classes.row} ${companyIndex === 0 && contactIndex === 0 ? classes.firstRow : null}`}>
                            <BodyCustomTableCell size='small'>{contact.firstName} {contact.lastName}</BodyCustomTableCell>
                            <BodyCustomTableCell size='small'>{contact.jobTitle}</BodyCustomTableCell>
                            <BodyCustomTableCell size='small'>{contact.department}</BodyCustomTableCell>
                            <BodyCustomTableCell size='small'>{contact.officePhone}</BodyCustomTableCell>
                            <BodyCustomTableCell size='small'>{contact.emailAddress}</BodyCustomTableCell>
                            {company !== 'pending' ? null : <BodyCustomTableCell size='small'>{contact.operationType.toUpperCase()}</BodyCustomTableCell>}
                          </CustomTableRow>
                          ))}
                      </TableBody>
                    </Table>
                  }
                </React.Fragment>
              )
            }
          )}
        </CardContent>
        <CardActions className={classes.cardFooter}>
          {/* <span className={classes.privacyStatement}>IPD Analytics cares about your <a className={classes.privacyLink} href='/'>privacy</a>.</span> */}
        </CardActions>
      </Card>
    )
  }
}

ManageUsersComponent.propTypes = {
  classes: PropTypes.object.isRequired,
  contacts: PropTypes.object
}

export default withStyles(styles)(ManageUsersComponent)
